/**
 * useShopContext Hook
 * Shop & Tenant Switching Logic
 *
 * @description Issue #284対応 - テナント・店舗切り替え統合フック
 * @integration useApiClient.ts、multiTenant.tsとの完全統合
 * @features テナント切り替え、店舗切り替え、階層表示、エラーハンドリング
 */

import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import * as Sentry from '@sentry/react';
import { useApiClient } from './useApiClient';
import { useAuthContext } from './useAuth';
import logger from '../utils/logger';
import { notifyError } from '../utils/errorHandling';
import { saveEncryptedToken } from '../utils/crypto';
import { TOKEN_KEYS } from '../services/auth/constants';
import { shopsControllerFindAll } from '../generated/api/shops/shops';
import type { ShopResponseDto } from '../generated/schemas';
import type { TenantContextFetchResult } from '../services/authService';
// storageUtils imports removed - Organization dead code cleanup

// Types
import type {
  TenantInfo,
  TenantContext,
  TenantLimits,
  TenantSettings,
} from '../types/multiTenant';

// Extended TenantContext with optional limits and settings
interface ExtendedTenantContext extends TenantContext {
  limits?: TenantLimits;
  settings?: TenantSettings;
}

// Type for available stores from TenantContext (extended with optional properties)
type AvailableStore = {
  id: string;
  name: string;
  code?: string;
  address?: string;
  phone?: string;
  email?: string;
  isActive?: boolean;
  createdAt?: string;
  updatedAt?: string;
};

type LoadStoresResult = 'populated' | 'empty' | 'failed';

/**
 * Store Information Interface
 * TenantContextのavailableStoresを拡張した完全なStore情報
 */
export interface StoreInfo {
  id: string;
  name: string;
  code?: string;
  address?: string;
  phone?: string;
  email?: string;
  isActive: boolean;
  tenantId: string;
  createdAt?: Date;
  updatedAt?: Date;
}

/**
 * ShopHierarchy Interface
 * 組織階層情報の表示用
 */
export interface ShopHierarchy {
  tenant: TenantInfo;
  currentStore?: StoreInfo;
  stats: {
    totalStores: number;
    activeStores: number;
    totalUsers: number;
    planLimits: {
      maxStores: number;
      maxUsers: number;
    };
  };
}

/**
 * useShopContext Hook Result Interface
 */
export interface UseShopContextResult {
  // 現在の組織情報
  currentTenant: TenantInfo | null;
  currentStore: StoreInfo | null;
  availableTenants: TenantInfo[];
  availableStores: StoreInfo[];
  organizationHierarchy: ShopHierarchy | null;

  // 切り替え機能
  switchTenant: (tenantId: string) => Promise<void>;
  switchStore: (storeId: string) => Promise<void>;
  refreshTenants: () => Promise<void>;
  refreshStores: () => Promise<void>;
  refreshTenantContext: () => Promise<TenantContextFetchResult>;

  // UI状態
  isLoading: boolean;
  isSwitching: boolean;
  error: string | null;

  // ユーティリティ
  canSwitchTenant: boolean;
  canSwitchStore: boolean;
  hasMultipleTenants: boolean;
  hasMultipleStores: boolean;
}

/**
 * Map an available store to StoreInfo
 */
function mapAvailableStoreToStoreInfo(
  store: AvailableStore,
  tenantId: string
): StoreInfo {
  return {
    id: store.id,
    name: store.name,
    code: store.code || '',
    address: store.address || '',
    phone: store.phone || '',
    email: store.email || '',
    isActive: store.isActive !== false,
    tenantId,
    createdAt: store.createdAt ? new Date(store.createdAt) : undefined,
    updatedAt: store.updatedAt ? new Date(store.updatedAt) : undefined,
  };
}

function mapShopResponseToStoreInfo(
  shop: ShopResponseDto,
  tenantId: string
): StoreInfo {
  return {
    id: shop.id,
    name: shop.name,
    code: '',
    address: '',
    phone: '',
    email: '',
    isActive: shop.status !== 'INACTIVE',
    tenantId,
    createdAt: shop.createdAt ? new Date(shop.createdAt) : undefined,
    updatedAt: shop.updatedAt ? new Date(shop.updatedAt) : undefined,
  };
}

async function loadStoresFromContextOrApi(
  tenantContext: TenantContext | null,
  tenantId: string
): Promise<StoreInfo[]> {
  const stores = tenantContext?.availableStores ?? [];
  if (stores.length > 0) {
    return stores.map((store: AvailableStore) =>
      mapAvailableStoreToStoreInfo(store, tenantId)
    );
  }

  const apiStores = await shopsControllerFindAll();
  return apiStores.map((shop) => mapShopResponseToStoreInfo(shop, tenantId));
}

function getErrorStatus(err: unknown): number | undefined {
  if (typeof err !== 'object' || err === null || !('response' in err)) {
    return undefined;
  }

  const response = (err as { response?: { status?: unknown } }).response;
  return typeof response?.status === 'number' ? response.status : undefined;
}

function getSentryTenantId(tenantId: string | null | undefined): string | undefined {
  return tenantId?.slice(0, 8);
}

function reportLoadStoresApiFailed(
  source: 'refresh' | 'init',
  tenantId: string | null | undefined,
  err: unknown
): void {
  Sentry.captureMessage('useShopContext.loadStores api failed', {
    level: 'warning',
    extra: {
      tenantId: getSentryTenantId(tenantId),
      source,
      status: getErrorStatus(err),
    },
  });
}

function reportAvailableStoresEmpty(
  source: 'refresh' | 'init',
  tenantContext: TenantContext | null
): void {
  if (!tenantContext) return;

  Sentry.captureMessage('availableStores empty', {
    level: 'warning',
    extra: {
      tenantId: getSentryTenantId(tenantContext.tenantId || tenantContext.id),
      currentStoreId: tenantContext.currentStoreId,
      source,
    },
  });
}

/**
 * useShopContext Hook Implementation
 */
export function useShopContext(): UseShopContextResult {
  // Dependencies
  const { tenantContext, checkAuthStatus } = useApiClient();
  const { user, isAuthenticated } = useAuthContext();
  const currentTenantId = tenantContext?.id || tenantContext?.tenantId;
  const tenantContextRef = useRef<TenantContext | null>(tenantContext);
  const currentTenantRef = useRef<TenantInfo | null>(null);
  const lastFetchedTenantIdRef = useRef<string | null>(null);
  const previousTenantIdRef = useRef<string | null>(null);

  // State
  const [availableTenants, setAvailableTenants] = useState<TenantInfo[]>([]);
  const [availableStores, setAvailableStores] = useState<StoreInfo[]>(() => {
    const stores = tenantContext?.availableStores ?? [];
    if (!currentTenantId || stores.length === 0) return [];

    return stores.map((store: AvailableStore) =>
      mapAvailableStoreToStoreInfo(store, currentTenantId)
    );
  });
  // 🔥 FIX: Initial value should be `true` to prevent race condition
  // When isLoading starts as `false`, PublicRoute may redirect before
  // organization data is loaded, causing incorrect redirects
  const [isLoading, setIsLoading] = useState(true);
  const [isSwitching, setIsSwitching] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    tenantContextRef.current = tenantContext;
  }, [tenantContext]);

  /**
   * Current organization info from context
   */
  const currentTenant = useMemo((): TenantInfo | null => {
    if (!tenantContext) return null;

    return {
      id: currentTenantId || tenantContext.id,
      name: tenantContext.name,
      subdomain: tenantContext.subdomain,
      planType: tenantContext.planType,
      features: tenantContext.features,
      billingStatus: tenantContext.billingStatus,
      status: 'active', // デフォルト値
      limits:
        (tenantContext as ExtendedTenantContext)?.limits ||
        ({
          maxUsers: 50,
          currentUsers: 1,
          maxCustomers: 10000,
          maxReservations: 50000,
          maxFiles: 1000,
          storageLimit: 1024,
          apiRequestsPerMinute: 100,
          apiRequestsPerHour: 1000,
          apiRequestsPerDay: 10000,
          maxIntegrations: 10,
          maxCustomFields: 50,
          maxReports: 20,
          limitsResetDate: new Date(),
          billingCycleStart: new Date(),
        } as TenantLimits),
      settings:
        (tenantContext as ExtendedTenantContext)?.settings ||
        ({
          brandColor: '#1976d2',
          enforceSSO: false,
          requireMFA: false,
          sessionTimeout: 480,
          passwordPolicy: {
            minLength: 8,
            requireUppercase: true,
            requireLowercase: true,
            requireNumbers: true,
            requireSpecialChars: false,
            maxAge: 90,
            preventReuse: 5,
          },
          enabledFeatures: tenantContext.features || [],
          customFields: [],
          allowedIntegrations: ['stripe', 'google-calendar'],
          webhookEndpoints: [],
          notificationSettings: {
            email: { enabled: true },
            sms: { enabled: false },
            push: { enabled: true },
            webhook: { enabled: false },
          },
          timezone: 'Asia/Tokyo',
          dateFormat: 'YYYY-MM-DD',
          currency: 'JPY',
          language: 'ja',
        } as TenantSettings),
    };
  }, [tenantContext, currentTenantId]);

  useEffect(() => {
    currentTenantRef.current = currentTenant;
  }, [currentTenant]);

  const tenantScopedAvailableStores = useMemo((): StoreInfo[] => {
    if (!currentTenantId) return [];

    return availableStores.filter((store) => store.tenantId === currentTenantId);
  }, [availableStores, currentTenantId]);

  const currentStore = useMemo((): StoreInfo | null => {
    if (!tenantContext?.currentStoreId || !tenantScopedAvailableStores.length) {
      return null;
    }

    return (
      tenantScopedAvailableStores.find(
        (store) => store.id === tenantContext.currentStoreId
      ) || null
    );
  }, [tenantContext?.currentStoreId, tenantScopedAvailableStores]);

  useEffect(() => {
    if (!tenantContext || !currentTenantId) {
      lastFetchedTenantIdRef.current = null;
      previousTenantIdRef.current = null;
      setAvailableStores([]);
      return;
    }

    const stores = tenantContext.availableStores ?? [];
    if (stores.length > 0) {
      setAvailableStores(
        stores.map((store: AvailableStore) =>
          mapAvailableStoreToStoreInfo(store, currentTenantId)
        )
      );
      previousTenantIdRef.current = currentTenantId;
      return;
    }

    if (previousTenantIdRef.current !== currentTenantId) {
      setAvailableStores([]);
      previousTenantIdRef.current = currentTenantId;
    }
  }, [tenantContext, currentTenantId]);

  /**
   * Organization hierarchy info
   */
  const organizationHierarchy = useMemo((): ShopHierarchy | null => {
    if (!currentTenant) return null;

    return {
      tenant: currentTenant,
      currentStore: currentStore || undefined,
      stats: {
        totalStores: tenantScopedAvailableStores.length,
        activeStores: tenantScopedAvailableStores.filter(
          (store) => store.isActive
        ).length,
        totalUsers: currentTenant.limits?.currentUsers || 1,
        planLimits: {
          maxStores: getPlanLimits(currentTenant.planType).maxStores,
          maxUsers: getPlanLimits(currentTenant.planType).maxUsers,
        },
      },
    };
  }, [currentTenant, currentStore, tenantScopedAvailableStores]);

  /**
   * Utility flags
   */
  const canSwitchTenant = useMemo(() => {
    return user?.role === 'owner';
  }, [user?.role]);

  const canSwitchStore = useMemo(() => {
    return tenantScopedAvailableStores.length > 1;
  }, [tenantScopedAvailableStores.length]);

  const hasMultipleTenants = availableTenants.length > 1;
  const hasMultipleStores = tenantScopedAvailableStores.length > 1;

  /**
   * Load available tenants
   */
  const refreshTenants = useCallback(async () => {
    if (!isAuthenticated || !canSwitchTenant) {
      return;
    }

    try {
      setError(null);
      // AuthService V2 API による実際のテナント一覧取得
      // 現在のユーザーがアクセス可能なテナント一覧を取得
      const { default: AuthService } = await import('../services/authService');
      const currentUser = AuthService.getCurrentUserV2();

      if (currentUser && currentTenant) {
        // 現在のテナント情報をベースに利用可能テナント一覧を構築
        const tenants: TenantInfo[] = [currentTenant];
        setAvailableTenants(tenants);
        logger.info('Available tenants loaded:', tenants.length);
      } else {
        // フォールバック: 空の配列
        setAvailableTenants([]);
        logger.info('Available tenants loaded: 0');
      }
    } catch (err) {
      const errorMsg = 'テナント一覧の取得に失敗しました';
      setError(errorMsg);
      notifyError(err, 'useShopContext.refreshTenants');
    }
  }, [isAuthenticated, canSwitchTenant, currentTenant]);

  /**
   * Load available stores for current tenant
   */
  const refreshStores = useCallback(async () => {
    if (!isAuthenticated || !currentTenantId) {
      return;
    }

    try {
      setError(null);
      // AuthService V2 API による実際の店舗一覧取得
      // テナントコンテキストから利用可能な店舗一覧を取得
      const { default: AuthService } = await import('../services/authService');
      const tenantContextForStores =
        AuthService.getCurrentTenantContextV2() ?? tenantContextRef.current;
      const mappedStores = await loadStoresFromContextOrApi(
        tenantContextForStores,
        currentTenantId
      );

      if (mappedStores.length > 0) {
        setAvailableStores(mappedStores);
        logger.info('Available stores loaded:', mappedStores.length);
      } else {
        // 店舗データが取得できない場合は空配列を設定
        setAvailableStores([]);
        reportAvailableStoresEmpty('refresh', tenantContextForStores);
        logger.warn('No available stores found in tenant context');
      }
    } catch (err) {
      reportLoadStoresApiFailed('refresh', currentTenantId, err);
      setError('店舗一覧の取得に失敗しました');
      notifyError(err, 'useShopContext.refreshStores');
    }
  }, [isAuthenticated, currentTenantId]);

  const refreshTenantContext = useCallback(async (): Promise<TenantContextFetchResult> => {
    if (!isAuthenticated || !user?.id) {
      return {
        ok: false,
        reason: 'auth_required',
        retryable: false,
      };
    }

    try {
      setError(null);
      const { default: AuthService } = await import('../services/authService');
      const storedTenantContext = AuthService.getCurrentTenantContextV2();
      if (storedTenantContext) {
        checkAuthStatus();
        return {
          ok: true,
          tenantContext: storedTenantContext,
        };
      }

      const fetchedTenantContext = await AuthService.fetchAndSaveTenantContextResult(
        user.id
      );
      checkAuthStatus();
      return fetchedTenantContext;
    } catch (err) {
      const errorMsg = 'テナント情報の再取得に失敗しました';
      setError(errorMsg);
      notifyError(err, 'useShopContext.refreshTenantContext');
      throw err;
    }
  }, [checkAuthStatus, isAuthenticated, user?.id]);

  /**
   * Switch to different tenant
   */
  const switchTenant = useCallback(
    async (tenantId: string) => {
      if (!canSwitchTenant || isSwitching) {
        return;
      }

      const targetTenant = availableTenants.find((t) => t.id === tenantId);
      if (!targetTenant) {
        setError('指定されたテナントが見つかりません');
        return;
      }

      try {
        setIsSwitching(true);
        setError(null);

        logger.info('Switching tenant:', {
          from: currentTenant?.id,
          to: tenantId,
        });

        // AuthService V2 API による実際のテナント切り替え
        await (
          await import('../services/authService')
        ).default.switchTenantV2({ tenantId });
        logger.info('Tenant switched via AuthService V2:', tenantId);

        // 店舗リストをリフレッシュ
        await refreshStores();

        logger.info('Tenant switched successfully:', tenantId);
      } catch (err) {
        const errorMsg = 'テナント切り替えに失敗しました';
        setError(errorMsg);
        notifyError(err, 'useShopContext.switchTenant');
      } finally {
        setIsSwitching(false);
      }
    },
    [
      canSwitchTenant,
      isSwitching,
      availableTenants,
      currentTenant?.id,
      refreshStores,
    ]
  );

  /**
   * Switch to different store
   */
  const switchStore = useCallback(
    async (storeId: string) => {
      if (!canSwitchStore || isSwitching) {
        return;
      }

      const targetStore = tenantScopedAvailableStores.find(
        (s) => s.id === storeId
      );
      if (!targetStore) {
        setError('指定された店舗が見つかりません');
        return;
      }

      try {
        setIsSwitching(true);
        setError(null);

        logger.info('Switching store:', {
          from: currentStore?.id,
          to: storeId,
        });

        // AuthService V2 API による実際の店舗切り替え
        // テナントコンテキストの店舗IDを更新
        const { default: AuthService } = await import(
          '../services/authService'
        );
        const currentTenantContext = AuthService.getCurrentTenantContextV2();

        if (currentTenantContext) {
          // テナントコンテキストを更新して店舗切り替えを反映
          const updatedContext = {
            ...currentTenantContext,
            currentStoreId: storeId,
          };

          // 暗号化ストレージに保存（セキュリティ強化）
          // 🔥 SECURITY FIX: Correct parameter order (data, key)
          saveEncryptedToken(
            JSON.stringify(updatedContext),
            TOKEN_KEYS.TENANT_CONTEXT
          );

          logger.info('Store switched via tenant context update:', storeId);
        } else {
          logger.warn('No tenant context available for store switch');
        }

        // NOTE: 実際のコンテキスト更新はuseApiClientで処理される想定

        logger.info('Store switched successfully:', storeId);
      } catch (err) {
        const errorMsg = '店舗切り替えに失敗しました';
        setError(errorMsg);
        notifyError(err, 'useShopContext.switchStore');
      } finally {
        setIsSwitching(false);
      }
    },
    [canSwitchStore, isSwitching, tenantScopedAvailableStores, currentStore?.id]
  );

  /**
   * Initialize data on mount with memory leak prevention
   * 🔥 FIX: Remove circular dependencies by removing function dependencies
   */
  useEffect(() => {
    if (!isAuthenticated) {
      lastFetchedTenantIdRef.current = null;
      setIsLoading(false);
      return;
    }

    if (
      currentTenantId &&
      lastFetchedTenantIdRef.current === currentTenantId
    ) {
      setIsLoading(false);
      return;
    }

    let isMounted = true; // 🧠 Memory Leak Prevention flag
    const tenantIdForInit = currentTenantId;

    setIsLoading(true);

    const initializeData = async () => {
      try {
        let storesLoaded: LoadStoresResult = 'failed';
        // 🔥 FIX: Call functions directly without dependencies to avoid infinite loops
        if (!canSwitchTenant) {
          // Skip tenant loading if user cannot switch tenants
          if (currentTenantId) {
            storesLoaded = await loadStoresDirectly();
          }
        } else {
          const [, loadedStores] = await Promise.all([
            loadTenantsDirectly(),
            currentTenantId
              ? loadStoresDirectly()
              : Promise.resolve<LoadStoresResult>('failed'),
          ]);
          storesLoaded = loadedStores;
        }

        if (storesLoaded === 'populated' && tenantIdForInit) {
          lastFetchedTenantIdRef.current = tenantIdForInit;
        }
      } catch (err) {
        if (tenantIdForInit) {
          lastFetchedTenantIdRef.current = null;
        }
        if (isMounted) {
          // 🧠 Only log if component is still mounted
          logger.error('Organization data initialization failed:', err);
          setError((currentError) =>
            currentError ?? '組織データの初期化に失敗しました'
          );
        }
      } finally {
        if (isMounted) {
          // 🧠 Only update state if component is still mounted
          setIsLoading(false);
        }
      }
    };

    // Direct loading functions to prevent dependency loops
    const loadTenantsDirectly = async () => {
      if (!isAuthenticated || !canSwitchTenant) return;

      try {
        setError(null);
        const { default: AuthService } = await import(
          '../services/authService'
        );
        const currentUser = AuthService.getCurrentUserV2();
        const latestCurrentTenant = currentTenantRef.current;

        if (currentUser && latestCurrentTenant) {
          const tenants: TenantInfo[] = [latestCurrentTenant];
          setAvailableTenants(tenants);
          logger.info('Available tenants loaded:', tenants.length);
        } else {
          setAvailableTenants([]);
          logger.info('Available tenants loaded: 0');
        }
      } catch (err) {
        const errorMsg = 'テナント一覧の取得に失敗しました';
        setError(errorMsg);
        notifyError(err, 'useShopContext.loadTenantsDirectly');
      }
    };

    const loadStoresDirectly = async (): Promise<LoadStoresResult> => {
      if (!isAuthenticated || !currentTenantId) return 'failed';

      try {
        setError(null);
        const { default: AuthService } = await import(
          '../services/authService'
        );
        const tenantContextForStores =
          AuthService.getCurrentTenantContextV2() ?? tenantContextRef.current;
        const mappedStores = await loadStoresFromContextOrApi(
          tenantContextForStores,
          currentTenantId
        );

        if (mappedStores.length > 0) {
          setAvailableStores(mappedStores);
          logger.info('Available stores loaded:', mappedStores.length);
          return 'populated';
        } else {
          // 店舗データが取得できない場合は空配列を設定
          setAvailableStores([]);
          reportAvailableStoresEmpty('init', tenantContextForStores);
          logger.warn('No available stores found in tenant context (init)');
          return 'empty';
        }
      } catch (err) {
        const errorMsg = '店舗一覧の取得に失敗しました';
        reportLoadStoresApiFailed('init', currentTenantId, err);
        setError(errorMsg);
        notifyError(err, 'useShopContext.loadStoresDirectly');
        return 'failed';
      }
    };

    initializeData();

    // 🧠 Cleanup function to prevent memory leaks
    return () => {
      isMounted = false;
    };
  }, [
    isAuthenticated,
    canSwitchTenant,
    currentTenantId,
    tenantContext,
  ]);

  return {
    // Current info
    currentTenant,
    currentStore,
    availableTenants,
    availableStores: tenantScopedAvailableStores,
    organizationHierarchy,

    // Actions
    switchTenant,
    switchStore,
    refreshTenants,
    refreshStores,
    refreshTenantContext,

    // State
    isLoading,
    isSwitching,
    error,

    // Utility flags
    canSwitchTenant,
    canSwitchStore,
    hasMultipleTenants,
    hasMultipleStores,
  };
}

/**
 * Get plan limits based on plan type
 */
function getPlanLimits(planType: string) {
  const limits = {
    light: { maxStores: 1, maxUsers: 5 },
    standard: { maxStores: 3, maxUsers: 15 },
    premium: { maxStores: 10, maxUsers: 50 },
    // Note: premium has higher limits (not unlimited)
  };

  return limits[planType as keyof typeof limits] || limits.light;
}

export default useShopContext;
